import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { getOrCreateDeviceToken } from "@/lib/deviceId";
import { loginForm, type LoginForm as LoginValues } from "@/features/auth/schemas";
import { useAdminLogin } from "@/features/auth/api/hooks";
import { isReverifyError } from "@/features/auth/api/authApi";
import { useAuthStore } from "@/features/auth/store";
import { IconInput } from "@/features/auth/components/IconInput";
import { PasswordInput } from "@/features/auth/components/PasswordInput";
import { AuthButton } from "@/features/auth/components/AuthButton";
import { AuthError } from "@/features/auth/components/AuthError";
import { AuthHeading } from "@/features/auth/components/AuthLayout";
import { AdminForgotFlow } from "./AdminForgotFlow";

export function AdminLoginForm() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const login = useAdminLogin();
  const [forgot, setForgot] = useState(false);
  const [newDevice, setNewDevice] = useState(false);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginForm),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit((values) => {
    login.mutate(
      { ...values, deviceToken: getOrCreateDeviceToken() },
      {
        onSuccess: ({ token, user }) => {
          setSession(token, user);
          navigate("/admin", { replace: true });
        },
        // New/unknown device → verify identity via the admin-recovery code flow (enrolls
        // this device on success). Same OTP-to-allowlist path as "Glemt passord?".
        onError: (err) => {
          if (isReverifyError(err)) {
            setNewDevice(true);
            setForgot(true);
          }
        },
      }
    );
  });

  if (forgot) {
    return (
      <AdminForgotFlow
        newDevice={newDevice}
        onBack={() => {
          setForgot(false);
          setNewDevice(false);
        }}
      />
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <AuthHeading title="Administrator" subtitle="Logg inn for å administrere tilgang" />

        {login.isError && <AuthError message={login.error.message} />}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <IconInput
                  icon={<Mail className="h-5 w-5" />}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="E-postadresse"
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormMessage className="ml-4 text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PasswordInput placeholder="Passord" autoComplete="current-password" {...field} />
              </FormControl>
              <FormMessage className="ml-4 text-red-400" />
            </FormItem>
          )}
        />

        <div className="text-right">
          <button
            type="button"
            onClick={() => setForgot(true)}
            className="text-sm text-white/70 underline transition-colors hover:text-white"
          >
            Glemt passord?
          </button>
        </div>

        <AuthButton type="submit" loading={login.isPending}>
          Logg inn
        </AuthButton>
      </form>
    </Form>
  );
}
